/* Copyright (c) 2018 Backpack Health LLC */
import * as React from 'react';
import * as _ from 'lodash';
import { Redirect } from 'react-router';

import { services } from '../../../../apiServices/services';
import { SurveyPageLayout } from './components/SurveyPageLayout';
import { GroupThemeMarkdown } from '../../../components/Markdown';
import DateUtil from '../../../../utilities/DateUtil';
import SurveyQuestionResponseModel from '../../../../models/SurveyQuestionResponseModel';

import RouteNames from '../../../appAuthorized/RouteNames';
import RenderSurveyQuestion from './RenderSurveyQuestion';
import SurveyHelpText from '../components/SurveyHelpText';

import { ScrollAwareContext } from '../../../components/ScrollAware';
import ContentWrapper from '../../../components/ContentWrapper';
import { GroupThemeedWarningModal } from '../../../components/modal/WarningModal';
import { useUIState } from '../../../hooks/useUIState';
import { useActiveProfile } from '../../../hooks/useProfile';
import { useConsent } from '../../../hooks/useConsent';
import ContentLoader from '../../../components/ContentLoader';
import { useNavigate } from '../../../hooks/useNavigate';

export const SurveyQuestion = (props) => {
  const isReviewing = props.match.path === RouteNames.surveyLastQuestion();
  const groupId = +props.match.params.groupId;
  const surveyId = +props.match.params.surveyId;
  const [, uiStateActions] = useUIState();
  const [activeProfileData] = useActiveProfile();
  const profileId = activeProfileData.data.id;
  const [{ isInformedConsentForbidden }] = useConsent(profileId);

  const [, navActions] = useNavigate();

  const surveyQuestionRef: any = React.createRef();
  const [response, setResponse] = React.useState(null);
  const [surveyInstanceQuestion, setSurveyInstanceQuestion] = React.useState(null);
  const [timeStart, setTimeStart] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [surveyComplete, setSurveyComplete] = React.useState(false);
  const [showValidationError, setShowValidationError] = React.useState(false);

  const surveyProgress = _.get(surveyInstanceQuestion, 'survey.completion_percentage', 0);

  const redirectToInformedConsent = () => {
    // no-op
  };

  const loadFirstQuestion = () => {
    return isReviewing ? services.surveys.getLastQuestion(profileId, surveyId) : services.surveys.getFirstQuestion(profileId, surveyId);
  };
  const setupQuestion = (newSurveyInstanceQuestion) => {
    setResponse({});
    ScrollAwareContext.resetScroll();
    setSurveyInstanceQuestion(newSurveyInstanceQuestion);
    setResponse({ ...newSurveyInstanceQuestion.response });
    setIsLoading(false);
  };
  const handleNavError = (apiError) => {
    setIsLoading(false);
    if (apiError.isUnavailableForLegalReasons()) {
      redirectToInformedConsent();
      return true;
    } else if (apiError.isLocked()) {
      uiStateActions.showDarkBigBlur(<SurveyAlreadyComplete groupId={groupId} />);
    }
    return false;
  };
  const prev = () => {
    setIsLoading(true);
    const questionId = surveyInstanceQuestion.survey_definition.section.question.id;
    services.surveys.getPrevQuestion(profileId, surveyId, questionId)
    .then((newSurveyInstanceQuestion) => {
      setupQuestion(newSurveyInstanceQuestion);
    })
    .catch((apiError) => {
      if (!handleNavError(apiError)) {
        navActions.goBackTo(RouteNames.surveyIntro(groupId, surveyId));
      }
    });
  };

  const submitAnswer = () => {
    const questionId = surveyInstanceQuestion.survey_definition.section.question.id;
    const timedResponse = {
      ...response,
      time_start: timeStart,
      time_end: DateUtil.getNowTimestamp(),
    };
    if (!SurveyQuestionResponseModel.isValid(response, surveyInstanceQuestion.survey_definition.section.question)) {
      setShowValidationError(true);
      return;
    }

    setIsLoading(true);
    setShowValidationError(false);

    return services.surveys.submitResponse(profileId, surveyId, questionId, timedResponse)
    .then(() => { setIsLoading(false); })
    .catch((apiError) => {
      if (!handleNavError(apiError)) {
        setSurveyComplete(apiError.isNotFound());
      }
    });
  };

  const next = async () => {
    const questionId = surveyInstanceQuestion.survey_definition.section.question.id;
    setIsLoading(true);

    await submitAnswer();

    services.surveys.getNextQuestion(profileId, surveyId, questionId)
      .then(newSurveyInstanceQuestion => {
        setTimeStart(DateUtil.getNowTimestamp());
        setupQuestion(newSurveyInstanceQuestion);
      })
      .catch((apiError) => {
        if (!handleNavError(apiError)) {
          setSurveyComplete(apiError.isNotFound());
        }
      });
  };

  React.useEffect(() => {
    uiStateActions.setActiveGroup(groupId);
    services.surveys.getSurvey(profileId, surveyId).then(s => {
      services.surveys.getDefinition(profileId, s.survey_definition_id).then(d => {
        loadFirstQuestion().then(sq => {
          setupQuestion(sq);
          setTimeStart(DateUtil.getNowTimestamp());
        }).catch((apiError) => {
          if (!handleNavError(apiError)) {
            setSurveyComplete(apiError.isNotFound());
          }
        });
      });
    });
  }, []);

  if (surveyComplete) { return <Redirect to={RouteNames.surveyReview(groupId, surveyId)} />; }
  if (isInformedConsentForbidden) { return <Redirect to={RouteNames.surveys(groupId)} />; }
  if (!surveyInstanceQuestion) { return null; }
  const question = surveyInstanceQuestion.survey_definition.section.question;
  const isValid = SurveyQuestionResponseModel.isValid(response, question);
  const categoryQuestion = question[question.category];

  return (
    <ContentLoader isContentLoaded={!isLoading} blockRenderUntilLoaded={false}>
    <SurveyPageLayout
      className="bkgd-color-csAppBGgray"
      navButtons={{
        prev,
        next,
        nextProps: {
          theme: 'group',
        },
      }}
      groupId={groupId}
      title={surveyInstanceQuestion.survey_definition.section.title}
      progressPercentage={surveyProgress}
      onDisabledClick={() => {
        setShowValidationError(SurveyQuestionResponseModel.hasAnswer(response));
        if (surveyQuestionRef.current) {
          surveyQuestionRef.current.onDisabledClick();
        }
      }}
      submitDisabled={!isValid}
      extendedHeader={() => (
        <div>
          <GroupThemeMarkdown text={question.text}/>
        </div>
      )}
    >
      <ContentWrapper>
        <div className="cmp-scrollaware__content">
          <SurveyHelpText help_text={categoryQuestion.help_text} />

          <RenderSurveyQuestion
            ref={surveyQuestionRef}
            question={question}
            response={response}
            onUpdate={setResponse}
            onSubmit={() => {
              if (isValid) {
                next();
              }
            }}
            profileId={profileId}
            showValidationError={showValidationError}
            clearError={() => {
              setShowValidationError(false);
            }}
          />
        </div>
      </ContentWrapper>
    </SurveyPageLayout>
    </ContentLoader>
  );
};

const SurveyAlreadyComplete = ({ groupId }) => {
  const [, navActions] = useNavigate();
  const [, uiActions] = useUIState();
  return (
    <GroupThemeedWarningModal
      phraseMessages={{
        title: { id: 'surveyAlreadyComplete.heading' },
        subheading: { id: 'surveyAlreadyComplete.subHeading' },
        body: { id: 'surveyAlreadyComplete.message' },
        button: { id: 'surveyAlreadyComplete.confirm.button.text' },
      }}
      onConfirm={() => {
        uiActions.dismissModal();
        navActions.goTo(RouteNames.surveys(groupId));
      }}
      hasCloseButton={false}
    />
  );
};
